import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Home, Utensils, Recycle, ChevronRight, Mail } from 'lucide-react';
import './HomeThird.css';
import Title from '../../components/Title/Title';

type ApplicationKey = 'electronics' | 'insulation' | 'food';

interface Application {
  icon: JSX.Element;
  title: string;
  items: string[];
}

const HomeThird: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ApplicationKey>('electronics');

  const applications: Record<ApplicationKey, Application> = {
    electronics: {
      icon: <Package size={48} />,
      title: 'EPS Transportverpackung für empfindliche Elektrogeräte',
      items: ['Handys & Smartphones', 'Kühlschränke & Großgeräte', 'Fernseher & Monitore'],
    },
    insulation: {
      icon: <Home size={48} />,
      title: 'Wärmedämmung und Isolierung aus Styropor',
      items: ['Fassadendämmung aus EPS', 'Bodenisolierung aus Polystyrol', 'Deckendämmung aus Styropor'],
    },
    food: {
      icon: <Utensils size={48} />,
      title: 'Transportverpackung und Isolierung aus Airpop für Lebensmittel',
      items: ['speziell: Fisch & Fleisch in Styropor-Boxen','Speisen aller Art in EPS-Verpackung', 'speziell: Wein in Airpop-Schutzverpackung','Getränke aller Art', 'Lieferdienste mit Styropor-Isolierung'],
    },
  };

  return (
    <section className="home-third-container">
      <div className="home-third-content">
        <Title animated="Styropor® und AIRPOP®" static="Unverzichtbar im Alltag" />
        
        <div className="home-third-intro">
          <p>
            <strong>Styropor®</strong> – auch bekannt als expandiertes Polystyrol (EPS) – ist der ehemalige Markenname und Gattungsbegriff für Schaumstoff, speziell für expandiertes (Poly)Styrol bzw. EPS.
            Seit einiger Zeit wird dieses Material unter dem neuen Begriff <strong>"AIRPOP®"</strong> geführt.
            Styropor Airpop ist aus dem modernen täglichen Leben nicht wegzudenken, es hat herausragend gute Eigenschaften. Es ist leicht, sicher, formbar, günstig, dämmt, isoliert und kann nun endlich auch vollständig recycelt werden. Die Anwendungen sind vielfältig und jeder nutzt es, z.B. als:
          </p>
          <p>
            Warum ist Styropor Recycling gerade in Hamburg so wichtig? Die Hansestadt gehört zu den wachstumsstärksten Städten Deutschlands. Durch rege Bautätigkeit und einen der größten Häfen Europas fallen enorme Mengen an EPS-Verpackungen und Styropor-Dämmmaterial an. Ohne fachgerechtes Recycling landen diese Materialien in der Verbrennung oder schlimmer – in der Umwelt. StyroR bietet Unternehmen eine nachhaltige Alternative: Wir sammeln, sortieren und recyceln Ihr Styropor – ob sauber, verunreinigt oder mit HBCD-Flammschutzmittel belastet. So schonen Sie nicht nur die Umwelt, sondern reduzieren gleichzeitig Ihre Entsorgungskosten. Professionelles Styropor Recycling ist der Schlüssel zu einer echten Kreislaufwirtschaft für Polystyrol.
          </p>
        </div>

        <div className="home-third-applications">
          <div className="home-third-tabs">
            {(Object.entries(applications) as [ApplicationKey, Application][]).map(([key, { icon, title }]) => (
              <button
                key={key}
                className={`home-third-tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                <div className="home-third-tab-icon">{icon}</div>
                <span>{title}</span>
              </button>
            ))}
          </div>
          <div className="home-third-tab-content">
            <h3>{applications[activeTab].title}</h3>
            <ul>
              {applications[activeTab].items.map((item: string, index: number) => (
                <li key={index}>
                  <ChevronRight size={20} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="home-third-recycling">
          <div className="home-third-recycling-content">
            <h3>Die Zukunft des Styropor Recyclings</h3>
            <p>Was, wenn Styropor Airpop seinen Zweck erfüllt hat? Bislang wurde es meistens verbrannt, also thermisch verwertet. Besonders, wenn es mit HBCD Flammschutzmittel belastet oder verunreinigt ist, war die Styropor Entsorgung problematisch.</p>
          <p>Heute wissen wir, Verbrennung ist in vielerlei Hinsicht nicht die beste Alternative – auch belastetes Styropor entsorgen geht besser.</p>
          <p>Und aus technischer Sicht gibt es nun ganz neue Möglichkeiten für professionelles EPS Recycling.</p>
          <p>StyroR setzt auf modernste Recycling-Verfahren für Styropor und EPS. Durch mechanische Aufbereitung und chemisches Recycling können wir selbst stark verunreinigtes oder HBCD-belastetes Styropor wieder in hochwertige Rohstoffe umwandeln. Das Ergebnis: Aus altem Polystyrol werden neue Dämmstoffe, Verpackungen und Baumaterialien – ein echter geschlossener Kreislauf. Für Unternehmen bedeutet das nicht nur geringere Entsorgungskosten, sondern auch einen messbaren Beitrag zum Klimaschutz. Jede Tonne Styropor, die recycelt statt verbrannt wird, spart erhebliche CO₂-Emissionen ein. Mit StyroR wird Styropor Recycling einfach, wirtschaftlich und nachhaltig.</p>
          </div>
          <div className="home-third-recycling-icon">
            <Recycle size={80} />
          </div>
        </div>

        <div className="home-third-cta">
          <h2>StyroR – Ihr Partner für Styropor Recycling in Hamburg</h2>
          <p>Ihr Spezialist und Ansprechpartner für gewinnsteigerndes Styropor Airpop Recycling-Management – EPS Entsorgung und Isolierung Recycling in Hamburg</p>
          <div className="home-third-slogan">
            <p>Sie retten die Umwelt  &  machen Geld</p>
          </div>
          <Link to="/kontakt/" className="home-third-button" title="Kontakt – StyroR Styropor Recycling Hamburg">
            <Mail size={20} />
            <span>Kontakt aufnehmen</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeThird;